import { useState, useEffect, useRef } from 'react';
import './Output.css';
import { Console } from './Console';

// JavaScript keywords for syntax highlighting
const JS_KEYWORDS = [
  'const', 'let', 'var', 'function', 'return', 'if', 'else', 'while', 'for',
  'try', 'catch', 'finally', 'throw', 'new', 'class', 'extends', 'import',
  'export', 'from', 'async', 'await', 'true', 'false', 'null', 'undefined',
  'this', 'break', 'continue', 'switch', 'case', 'default', 'typeof',
  'instanceof', 'in', 'of', 'delete', 'void', 'yield', 'static', 'get', 'set'
];

const JS_BUILTINS = ['console', 'log', 'error', 'warn', 'prompt', 'alert'];

type OutputTab = 'js' | 'console';

interface TranspileError {
  message: string;
  line: number;
}

interface OutputProps {
  code: string;
  transpile: (code: string) => { js: string; errors: TranspileError[] };
  debounceMs?: number;
}

/**
 * Escapes HTML special characters
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Applies JavaScript syntax highlighting
 */
function highlightJS(code: string): string {
  let result = escapeHtml(code);

  // Highlight strings
  result = result.replace(
    /(["'`])(?:(?!\1)[^\\]|\\.)*\1/g,
    '<span class="js-string">$&</span>'
  );

  // Highlight comments (single-line)
  result = result.replace(
    /(\/\/[^\n]*)/g,
    '<span class="js-comment">$1</span>'
  );

  // Highlight multi-line comments
  result = result.replace(
    /(\/\*[\s\S]*?\*\/)/g,
    '<span class="js-comment">$1</span>'
  );

  // Highlight numbers
  result = result.replace(
    /\b(\d+\.?\d*)\b/g,
    '<span class="js-number">$1</span>'
  );

  // Highlight keywords
  const keywordPattern = new RegExp(`\\b(${JS_KEYWORDS.join('|')})\\b`, 'g');
  result = result.replace(keywordPattern, '<span class="js-keyword">$1</span>');

  // Highlight builtins
  const builtinPattern = new RegExp(`\\b(${JS_BUILTINS.join('|')})\\b`, 'g');
  result = result.replace(builtinPattern, '<span class="js-builtin">$1</span>');

  // Highlight function calls
  result = result.replace(
    /\b([a-zA-Z_][a-zA-Z0-9_]*)(?=\s*\()/g,
    (match, name) => {
      if (JS_KEYWORDS.includes(name) || JS_BUILTINS.includes(name)) {
        return match;
      }
      return `<span class="js-function">${name}</span>`;
    }
  );

  return result;
}

/**
 * Debounce hook
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function Output({ code, transpile, debounceMs = 150 }: OutputProps) {
  const [activeTab, setActiveTab] = useState<OutputTab>('js');
  const [jsOutput, setJsOutput] = useState<string>('');
  const [errors, setErrors] = useState<TranspileError[]>([]);
  const outputRef = useRef<HTMLPreElement>(null);

  // Debounce the input code to avoid excessive transpilation
  const debouncedCode = useDebounce(code, debounceMs);

  // Transpile code when it changes (debounced)
  useEffect(() => {
    if (!debouncedCode.trim()) {
      setJsOutput('// Transpiled JavaScript will appear here');
      setErrors([]);
      return;
    }

    try {
      const result = transpile(debouncedCode);
      setJsOutput(result.js || '// Empty output');
      setErrors(result.errors);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setJsOutput(`// Transpilation error`);
      setErrors([{ message: errorMessage, line: 0 }]);
    }
  }, [debouncedCode, transpile]);

  // Handle switching to console tab when running
  const handleRunStart = () => {
    setActiveTab('console');
  };

  // Calculate line numbers
  const jsLines = jsOutput.split('\n').length;

  return (
    <div className="output-container">
      {/* Tab header */}
      <div className="output-tabs">
        <div className="tab-buttons">
          <button
            className={`tab-button ${activeTab === 'js' ? 'active' : ''}`}
            onClick={() => setActiveTab('js')}
          >
            JavaScript Output
          </button>
          <button
            className={`tab-button ${activeTab === 'console' ? 'active' : ''}`}
            onClick={() => setActiveTab('console')}
          >
            Console
          </button>
        </div>
      </div>

      {/* Error display */}
      {errors.length > 0 && (
        <div className="error-panel">
          {errors.map((error, index) => (
            <div key={index} className="error-item">
              <span className="error-icon">!</span>
              <span className="error-message">
                {error.line > 0 && <span className="error-line">Ligne {error.line}:</span>}
                {error.message}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Output content */}
      <div className="output-content">
        {activeTab === 'js' && (
          <div className="js-output-panel">
            <div className="output-line-numbers">
              {Array.from({ length: jsLines }, (_, i) => (
                <div key={i} className="output-line-number">{i + 1}</div>
              ))}
            </div>
            <pre
              ref={outputRef}
              className="output-code"
              dangerouslySetInnerHTML={{
                __html: errors.length > 0
                  ? escapeHtml(jsOutput)
                  : highlightJS(jsOutput)
              }}
            />
          </div>
        )}
        {activeTab === 'console' && (
          <Console
            jsCode={jsOutput}
            onRun={handleRunStart}
            disabled={errors.length > 0 || !jsOutput || jsOutput.startsWith('//')}
          />
        )}
      </div>
    </div>
  );
}

export type { TranspileError, OutputTab };
